import React, { useRef, useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ThemeContext from '../../contexts/ThemeContext';
import DeleteIcon from '../../images/DeleteIcon';
import Icon from '../Icon';
import { FieldLabel, isPreviewableAttachment } from './SummaryShared';
import TruncatedFileName from '../TruncatedFileName';
import i18n from '../../i18n';
import {
  TRANSLATION_MAX_SIZE,
  TRANSLATION_ACCEPT_STRING,
  TRANSLATION_ALLOWED_EXTENSIONS,
  isTranslationAllowedFileType,
  getTranslationAllowedTypesDisplayString,
  TRANSLATION_EXTENSION_TO_MIME_MAP,
  isTranslationSignatureValid,
} from '../../config/attachmentConfig';
import {
  getTranslatedFileUploadUrl,
  confirmTranslatedFileUpload,
  deleteTranslatedFile,
} from '../../services/translationsService';
import { uploadFileToS3 } from '../../services/prebookService';
import { showError } from '../../services/toastService';
import { formatFileSize } from '../../utils/formatters';
import TransferProgressBar from '../TransferProgressBar';
import useFileTransferProgress from '../../hooks/useFileTransferProgress';

const FileListContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const FileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ $colors }) => $colors.hoverColor};
  }
`;

const FileNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const FileNameText = styled(TruncatedFileName)`
  font-size: ${({ $layout }) => $layout.mediumFontSize}px;
  font-weight: 500;
  color: ${({ $colors }) => $colors.twilight};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  text-decoration: ${({ $clickable }) => $clickable ? 'underline' : 'none'};
  text-decoration-color: transparent;
  transition: text-decoration-color 0.2s;

  ${({ $clickable }) => $clickable && `
    &:hover {
      text-decoration-color: currentColor;
    }
  `}
`;

const DeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid ${({ $colors }) => $colors.borderColorMedium};
  background: white;
  border-radius: 20px;
  color: ${({ $colors }) => $colors.twilight};
  font-size: ${({ $layout }) => $layout.smallFontSize}px;
  font-weight: 500;
  letter-spacing: 0.15px;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 8px;
  transition: all 0.2s;
  white-space: nowrap;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    border-color: ${({ $colors }) => $colors.errorColor};
    color: ${({ $colors }) => $colors.errorColor};
    background-color: rgba(234, 126, 143, 0.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PreviewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: 1px solid ${({ $colors }) => $colors.borderColor};
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 8px;
  font-size: ${({ $layout }) => $layout.smallFontSize}px;
  font-weight: 500;
  color: ${({ $colors }) => $colors.twilight};
  transition: all 0.2s;

  &:hover {
    background: ${({ $colors }) => $colors.hoverColor};
    border-color: ${({ $colors }) => $colors.twilight};
  }
`;

const HelpText = styled.div`
  font-size: ${({ $layout }) => $layout.smallFontSize}px;
  color: ${({ $colors }) => $colors.placeholderColor};
  margin: 4px 0 8px;
`;

const UploadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  background: ${({ $colors }) => $colors.tulkaMainColor};
  color: white;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 12px;
  transition: opacity 0.2s;
  align-self: flex-start;

  &:hover { opacity: 0.8; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const UploadingRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 0;
`;

const UploadingName = styled.span`
  font-size: ${({ $layout }) => $layout.mediumFontSize}px;
  font-weight: 500;
  color: ${({ $colors }) => $colors.twilight};
`;

const readFileSignatureBytes = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target.result);
      const hex = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
      resolve(hex);
    };
    reader.onerror = () => resolve('');
    reader.readAsArrayBuffer(file.slice(0, 16));
  });

const getFileExtension = (filename) => {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return '';
  return filename.substring(lastDot).toLowerCase();
};

const TranslatedMaterials = ({ translationId, translatedFiles, onRefresh, readOnly, onDownload, onPreview, downloadProgress }) => {
  const { colors, layout } = useContext(ThemeContext);
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const uploadProgress = useFileTransferProgress();
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Reset input so the same file can be selected again
    e.target.value = '';

    const validFiles = [];
    for (const file of files) {
      const ext = getFileExtension(file.name);
      if (!ext || !TRANSLATION_ALLOWED_EXTENSIONS.includes(ext)) {
        showError(`${file.name}: ${i18n('attachment_type_not_allowed') || 'File type not allowed'}`);
        continue;
      }
      // Prefer the canonical MIME for the (already validated) extension over the
      // browser-reported file.type, which is unreliable for audio formats
      // (e.g. .m4a is often reported as audio/x-m4a, which fails the allow-list).
      const mimeType = TRANSLATION_EXTENSION_TO_MIME_MAP[ext] || file.type || '';
      if (!isTranslationAllowedFileType(mimeType)) {
        showError(`${file.name}: ${i18n('attachment_invalid_type') || 'Invalid file type'}. ${getTranslationAllowedTypesDisplayString()}`);
        continue;
      }
      if (file.size > TRANSLATION_MAX_SIZE) {
        showError(`${file.name}: ${i18n('attachment_too_large') || 'File too large'} (max ${formatFileSize(TRANSLATION_MAX_SIZE)})`);
        continue;
      }
      const signatureHex = await readFileSignatureBytes(file);
      if (!isTranslationSignatureValid(signatureHex, mimeType, ext)) {
        showError(`${file.name}: ${i18n('attachment_invalid_signature') || 'Invalid file'}`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    setIsUploading(true);

    const items = validFiles.map((file, index) => ({
      key: `${index}-${file.name}-${file.size}`,
      filename: file.name,
      size: file.size,
      file,
    }));
    setUploadingFiles(items);
    items.forEach((it) => uploadProgress.start(it.key, it.size));

    try {
      const results = await Promise.allSettled(
        items.map(async (it) => {
          try {
            const { uploadUrl, attachmentId } = await getTranslatedFileUploadUrl(translationId, {
              filename: it.file.name,
              fileSize: it.file.size,
              contentType: it.file.type,
            });
            await uploadFileToS3(uploadUrl, it.file, {
              onProgress: (loaded, total) => uploadProgress.update(it.key, loaded, total || it.size),
            });
            await confirmTranslatedFileUpload(translationId, attachmentId);
            uploadProgress.complete(it.key);
          } catch (uploadErr) {
            uploadProgress.fail(it.key);
            throw uploadErr;
          }
        })
      );

      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length > 0) {
        failed.forEach(r => console.error('Failed to upload translated file:', r.reason));
        showError(i18n('requestErrorMessage'));
      }
    } catch (err) {
      console.error('Failed to upload translated files:', err);
      showError(err.message || i18n('requestErrorMessage'));
    }

    if (mountedRef.current) {
      await onRefresh();
      setUploadingFiles([]);
      uploadProgress.reset();
      setIsUploading(false);
    }
  };

  const handleDelete = async (attachmentId) => {
    setDeletingId(attachmentId);
    try {
      await deleteTranslatedFile(translationId, attachmentId);
      await onRefresh();
    } catch (err) {
      console.error('Failed to delete translated file:', err);
      showError(err.message || i18n('requestErrorMessage'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <FieldLabel style={{ marginBottom: 4 }}>
        {i18n('translation_translated_materials')}
      </FieldLabel>

      {translatedFiles && translatedFiles.length > 0 && (
        <FileListContainer>
          {translatedFiles.map((file, index) => {
            const dp = downloadProgress ? downloadProgress[file._id] : undefined;
            const showDp = !!dp && (dp.status === 'active' || dp.status === 'error');
            return (
              <React.Fragment key={file._id || index}>
                <FileRow $colors={colors}>
                  <FileNameRow>
                    <Icon icon="attach_file" iconLib="material" iconColor={colors.twilight} iconSize={18} withoutContainer={true} />
                    <FileNameText
                      $colors={colors}
                      $layout={layout}
                      $clickable={!!onDownload}
                      onClick={onDownload ? () => onDownload(file) : undefined}
                      filename={file.filename}
                      fileSize={file.fileSize || file.size}
                    />
                  </FileNameRow>
                  {onPreview && isPreviewableAttachment(file) && (
                    <PreviewButton
                      $colors={colors}
                      $layout={layout}
                      onClick={() => onPreview(file)}
                      aria-label={`${i18n('preview') || 'Preview'} ${file.filename}`}
                      title={i18n('preview') || 'Preview'}
                    >
                      <Icon
                        icon="visibility"
                        iconLib="material"
                        iconColor={colors.twilight}
                        iconSize={18}
                      />
                      {i18n('preview') || 'Preview'}
                    </PreviewButton>
                  )}
                  {!readOnly && (
                    <DeleteButton
                      $colors={colors}
                      $layout={layout}
                      onClick={() => handleDelete(file._id)}
                      disabled={deletingId === (file._id)}
                      title={`${i18n('delete') || 'Delete'} ${file.filename}`}
                    >
                      <DeleteIcon color={colors.twilight} size={16} />
                      {i18n('delete') || 'Delete'}
                    </DeleteButton>
                  )}
                </FileRow>
                {showDp && (
                  <TransferProgressBar
                    loaded={dp.loaded}
                    total={dp.total}
                    status={dp.status}
                    label={dp.status === 'error' ? i18n('transfer_failed') : i18n('downloading')}
                  />
                )}
              </React.Fragment>
            );
          })}
        </FileListContainer>
      )}

      {uploadingFiles.length > 0 && (
        <FileListContainer>
          {uploadingFiles.map((it) => {
            const p = uploadProgress.progress[it.key];
            return (
              <UploadingRow key={it.key}>
                <UploadingName $colors={colors} $layout={layout}>{it.filename}</UploadingName>
                <TransferProgressBar
                  loaded={p ? p.loaded : 0}
                  total={p ? p.total : it.size}
                  status={p ? p.status : 'active'}
                  label={p && p.status === 'error' ? i18n('transfer_failed') : i18n('uploading')}
                />
              </UploadingRow>
            );
          })}
        </FileListContainer>
      )}

      {!readOnly && (
        <>
          <HelpText $colors={colors} $layout={layout}>
            {i18n('translation_order_accepted_types')}: {getTranslationAllowedTypesDisplayString()}. Max {formatFileSize(TRANSLATION_MAX_SIZE)}.
          </HelpText>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={TRANSLATION_ACCEPT_STRING}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <UploadButton
            $colors={colors}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <Icon icon="upload" iconLib="material" iconColor="white" iconSize={18} />
            {isUploading ? '...' : i18n('translation_upload_material')}
          </UploadButton>
        </>
      )}
    </>
  );
};

TranslatedMaterials.propTypes = {
  translationId: PropTypes.string.isRequired,
  translatedFiles: PropTypes.array,
  onRefresh: PropTypes.func,
  readOnly: PropTypes.bool,
  onDownload: PropTypes.func,
  onPreview: PropTypes.func,
  downloadProgress: PropTypes.object,
};

export default TranslatedMaterials;
